import { useId, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, Loader2 } from "lucide-react";

import github from "../../assets/brands/github.svg";
import githubDark from "../../assets/brands/github-dark.svg";
import google from "../../assets/brands/google.svg";
import { Button } from "@/v2/ui/button";
import { Field, FieldError, FieldLabel, FieldSeparator } from "@/v2/ui/field";
import { Input } from "@/v2/ui/input";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/v2/ui/input-group";
import { cn } from "@/v2/lib/utils";

export const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

/** What's wrong with a typed address, or nothing */
export function emailProblem(email: string): string | undefined {
    if (!email.trim()) return "Enter your email address.";
    if (!EMAIL_PATTERN.test(email.trim())) return "That doesn't look like an email address.";
}

/** The page's title and one line under it */
export function AuthHeader({ title, children }: { title: string; children?: ReactNode }) {
    return (
        <header className="mb-8">
            <h1 className="font-display text-[1.75rem] leading-tight font-semibold tracking-tight">{title}</h1>
            {children && <p className="mt-2 text-sm leading-relaxed text-text-2 [overflow-wrap:anywhere]">{children}</p>}
        </header>
    );
}

/** A message above the form: a tint, never a coloured edge */
export function Notice({ tone = "bad", title, children, className }: { tone?: "bad" | "info"; title: ReactNode; children?: ReactNode; className?: string }) {
    return (
        <div role={tone === "bad" ? "alert" : "status"} className={cn(
            "mb-6 rounded-xl px-4 py-3 text-sm leading-relaxed",
            tone === "bad" ? "bg-bad-tint text-bad-ink" : "bg-info-tint text-info-ink",
            className,
        )}>
            <p className="font-medium">{title}</p>
            {children && <p className="mt-0.5 [&_a]:font-medium [&_a]:underline [&_a]:underline-offset-2">{children}</p>}
        </div>
    );
}

/** GitHub and Google, then a divider into the email form */
export function ProviderButtons({ onGitHub, onGoogle, divider }: { onGitHub: () => void; onGoogle: () => void; divider: string }) {
    return (
        <>
            <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" className="h-10 rounded-xl" onClick={onGitHub}>
                    <img src={github} alt="" className="size-4 dark:hidden" />
                    <img src={githubDark} alt="" className="hidden size-4 dark:block" />
                    GitHub
                </Button>
                <Button variant="outline" className="h-10 rounded-xl" onClick={onGoogle}>
                    <img src={google} alt="" className="size-4" />
                    Google
                </Button>
            </div>
            <FieldSeparator className="my-6 text-xs">{divider}</FieldSeparator>
        </>
    );
}

/** A labelled text field with its error under it */
export function TextField({ label, error, aside, className, ...input }: React.ComponentProps<typeof Input> & {
    label: string;
    error?: string;
    /** A link on the label's line, e.g. "Forgot password?" */
    aside?: ReactNode;
}) {
    const id = useId();
    return (
        <Field data-invalid={!!error || undefined} className={cn("gap-1.5", className)}>
            <div className="flex items-baseline justify-between gap-3">
                <FieldLabel htmlFor={id}>{label}</FieldLabel>
                {aside}
            </div>
            <Input id={id} aria-invalid={!!error || undefined} {...input} />
            {error && <FieldError className="text-xs text-bad-ink">{error}</FieldError>}
        </Field>
    );
}

/** A password field with a show/hide toggle */
export function PasswordField({ label, value, onChange, error, aside, autoComplete, autoFocus, hint }: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    error?: string;
    aside?: ReactNode;
    autoComplete: "current-password" | "new-password";
    autoFocus?: boolean;
    hint?: string;
}) {
    const id = useId();
    const [shown, setShown] = useState(false);
    return (
        <Field data-invalid={!!error || undefined} className="gap-1.5">
            <div className="flex items-baseline justify-between gap-3">
                <FieldLabel htmlFor={id}>{label}</FieldLabel>
                {aside}
            </div>
            <InputGroup>
                <InputGroupInput
                    id={id}
                    type={shown ? "text" : "password"}
                    autoComplete={autoComplete}
                    autoFocus={autoFocus}
                    aria-invalid={!!error || undefined}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                />
                <InputGroupAddon align="inline-end">
                    <InputGroupButton size="icon-xs" onClick={() => setShown(s => !s)} aria-label={shown ? "Hide password" : "Show password"}>
                        {shown ? <EyeOff /> : <Eye />}
                    </InputGroupButton>
                </InputGroupAddon>
            </InputGroup>
            {error
                ? <FieldError className="text-xs text-bad-ink">{error}</FieldError>
                : hint && <p className="text-xs text-text-3">{hint}</p>}
        </Field>
    );
}

/** The form's one primary action, full width. The label stays while it works; a spinner shows it's busy */
export function SubmitButton({ busy, disabled, children, onClick, type = "submit" }: {
    busy?: boolean;
    disabled?: boolean;
    children: ReactNode;
    onClick?: () => void;
    type?: "submit" | "button";
}) {
    return (
        <Button type={type} onClick={onClick} disabled={busy || disabled} className="h-10 w-full rounded-xl text-sm">
            {children}
            {busy && <Loader2 className="animate-spin" />}
        </Button>
    );
}

/** A small link in the label row or under the form */
export function AuthLink({ to, children, className }: { to: string; children: ReactNode; className?: string }) {
    return <Link to={to} className={cn("text-xs font-medium text-text-2 hover:text-text", className)}>{children}</Link>;
}

/** The line under the form that points somewhere else: "Don't have an account? Create one" */
export function AuthFooterLine({ children }: { children: ReactNode }) {
    return <p className="mt-8 text-center text-sm text-text-2 [&_a]:font-medium [&_a]:text-text [&_a:hover]:underline [&_a]:underline-offset-4">{children}</p>;
}

export function BackToSignIn() {
    return (
        <p className="mt-8 text-center">
            <Link to="/auth/login" className="inline-flex items-center gap-1.5 text-sm font-medium text-text-2 hover:text-text">
                <ArrowLeft className="size-4" /> Back to sign in
            </Link>
        </p>
    );
}

/** A finished step (email sent, password set, verified): an icon, a title, a sentence and what to do next */
export function AuthStatus({ icon, tone, title, children, actions }: {
    icon: ReactNode;
    tone: "ok" | "bad" | "info";
    title: string;
    children: ReactNode;
    actions?: ReactNode;
}) {
    return (
        <div role="status">
            <span className={cn(
                "mb-6 grid size-12 place-items-center rounded-full [&_svg]:size-6",
                tone === "ok" && "bg-ok-tint text-ok-ink",
                tone === "bad" && "bg-bad-tint text-bad-ink",
                tone === "info" && "bg-info-tint text-info-ink",
            )}>{icon}</span>
            <h1 className="font-display text-[1.75rem] leading-tight font-semibold tracking-tight">{title}</h1>
            <p className="mt-2 text-sm leading-relaxed text-text-2 [overflow-wrap:anywhere]">{children}</p>
            {actions && <div className="mt-8 flex flex-col gap-3">{actions}</div>}
        </div>
    );
}
